import { createSelector } from 'reselect'
import moment from 'moment'

export const selectUser = state => state?.user || {}

export const selectBusiness = createSelector(selectUser, user => user.business || {})

export const selectIsAdmin = createSelector(selectUser, user => user.role === 'Business Owner')

export const selectUserPermission = createSelector(selectUser, user => user.user_permission || {})

export const selectIsMainBusiness = createSelector(selectUser, user => user.is_main_business)

export const selectBusinessPermission = createSelector(selectUser, user => user.permission || {})

export const selectFranchise = createSelector(selectUser, user => user.franchise || {})

export const selectMainBusiness = createSelector(
  selectFranchise,
  franchise => franchise.main_business,
)

export const canSendSms = createSelector(selectBusiness, business => {
  return !business.permission?.restrict_sms
})

export const canManageDashboard = createSelector(selectIsMainBusiness, isMainBusiness => {
  return isMainBusiness
})

export const canEnroll = createSelector(
  [selectIsAdmin, selectIsMainBusiness, selectBusinessPermission, selectUserPermission],
  (isAdmin, isMainBusiness, permission, userPermission) => {
    return (isMainBusiness && isAdmin) || (permission.allow_enroll && userPermission.allow_enroll)
  },
)

export const canRemoveCustomer = createSelector(
  [selectIsAdmin, selectIsMainBusiness, selectBusiness],
  (isAdmin, isMainBusiness, business) => {
    return isMainBusiness && isAdmin && business.can_remove_customer
  },
)

export const canExportCustomer = createSelector(
  [selectIsAdmin, selectIsMainBusiness, selectBusiness],
  (isAdmin, isMainBusiness, business) => {
    return isMainBusiness && isAdmin && business.can_export_customer
  },
)

export const canIssueGift = createSelector(
  [selectIsAdmin, selectIsMainBusiness, selectBusinessPermission, selectUserPermission],
  (isAdmin, isMainBusiness, permission, userPermission) => {
    return (
      (isMainBusiness && isAdmin) ||
      (permission.allow_issue_gift && userPermission.allow_issue_gift)
    )
  },
)

export const canManagePoints = createSelector(
  [selectIsAdmin, selectIsMainBusiness, selectBusinessPermission, selectUserPermission],
  (isAdmin, isMainBusiness, permission, userPermission) => {
    return (
      (isMainBusiness && isAdmin) ||
      (permission.allow_manage_points && userPermission.allow_manage_points)
    )
  },
)

export const canManagePosts = createSelector(
  [selectIsAdmin, selectIsMainBusiness, selectUserPermission],
  (isAdmin, isMainBusiness, userPermission) => {
    return (isMainBusiness && isAdmin) || (isMainBusiness && userPermission.allow_manage_posts)
  },
)

export const canManageInbox = createSelector(
  [selectIsAdmin, selectIsMainBusiness, selectBusinessPermission, selectUserPermission],
  (isAdmin, isMainBusiness, permission, userPermission) => {
    return (
      (isMainBusiness && isAdmin && permission.allow_manage_inbox) ||
      (isMainBusiness && permission.allow_manage_inbox && userPermission.allow_manage_inbox)
    )
  },
)

export const canManageReports = createSelector(
  [selectIsAdmin, selectUserPermission],
  (isAdmin, userPermission) => {
    return isAdmin || userPermission.allow_manage_reports
  },
)

export const canManageSettings = createSelector([selectIsAdmin], isAdmin => {
  return isAdmin
})

export const canManagePhoneAssistant = createSelector(
  [selectIsAdmin, selectIsMainBusiness, selectBusinessPermission],
  (isAdmin, isMainBusiness, permission) => {
    return isMainBusiness && isAdmin && permission.allow_manage_inbox
  },
)

export const canManageBilling = createSelector(
  [selectIsAdmin, selectIsMainBusiness, selectBusinessPermission],
  (isAdmin, isMainBusiness, permission) => {
    return (isAdmin && isMainBusiness) || (isAdmin && permission.allow_manage_billing)
  },
)

const selectPartners = createSelector(selectUser, user => user.partnersData?.partner || {})

export const canManageGift = createSelector(selectPartners, partners => !partners.shopify)

export const canManageSales = createSelector(
  [selectIsAdmin, selectBusinessPermission],
  (isAdmin, permission) => {
    return isAdmin && permission.allow_manage_sales
  },
)

export const shopifyWidgetEnableUrl = createSelector(selectPartners, partners => {
  let url
  if (partners.shopify && !partners.shopify.is_customer_widget_installed) {
    url = partners.shopify.customer_widget_embed_url
  }
  return url
})

export const canManageSocialMediaIntegrations = createSelector(
  [selectBusiness, selectPartners],
  (business, partners) => {
    return (
      moment(business.created_at).isBefore(moment('2022-11-01')) &&
      partners &&
      (partners.facebook || partners.google || partners.instagram || partners.twitter)
    )
  },
)

export const canManageAllowance = createSelector([selectBusinessPermission], permission => {
  return permission.allow_manage_allowance
})

export const canIssueAllowance = createSelector(
  [selectIsAdmin, selectIsMainBusiness],
  (isAdmin, isMainBusiness) => {
    return isMainBusiness && isAdmin
  },
)
